/**
 * Every colour in the brand schema, in one place.
 *
 * The brand form, the create/edit modal, the JSON import and the save payload are all
 * generated from this list. Adding a colour to the schema means adding one row here —
 * previously each colour had to be repeated in five separate places, which is how
 * `card`, `sheet`, the greys and the gradients ended up unreachable from the UI while
 * still being written to the backend.
 *
 * `token`  — the key inside brand.colors / brand.colors_dark
 * `light`  — default used for brand.colors
 * `dark`   — default used for brand.colors_dark
 *
 * Form control names are derived, never hand-written:
 *   light -> `${token}_color`        e.g. card_border_color
 *   dark  -> `dark_${token}_color`   e.g. dark_card_border_color
 */
export interface BrandColorToken {
  token: string;
  label: string;
  hint: string;
  light: string;
  dark: string;
  /** Only primary is required — a brand with no primary colour is meaningless. */
  required?: boolean;
}

export interface BrandColorGroup {
  title: string;
  icon: string;
  /** Shown as a tooltip on the group heading, explaining what the group controls. */
  hint: string;
  tokens: BrandColorToken[];
}

export const BRAND_COLOR_GROUPS: BrandColorGroup[] = [
  {
    title: 'Brand',
    icon: 'fa-solid fa-droplet',
    hint: 'Your core brand colours. Primary drives buttons, highlights and the main call to action.',
    tokens: [
      { token: 'primary', label: 'Primary', hint: 'Main brand colour — buttons and highlights', light: '#FCBA03', dark: '#FCBA03', required: true },
      { token: 'secondary', label: 'Secondary', hint: 'Supporting brand colour', light: '#122331', dark: '#FFD451' },
      { token: 'accent', label: 'Accent', hint: 'Links and small emphasis', light: '#FCBA03', dark: '#FCBA03' },
      { token: 'on_primary', label: 'On Primary', hint: 'Text/icon colour placed on top of the primary colour', light: '#1D1D1B', dark: '#1D1D1B' },
    ],
  },
  {
    title: 'Surfaces',
    icon: 'fa-solid fa-layer-group',
    hint: 'Background layers of the page — the page itself, cards, panels and slide-up sheets.',
    tokens: [
      { token: 'bg', label: 'Page Background', hint: 'Behind everything else', light: '#F7F6F9', dark: '#0D1620' },
      { token: 'surface', label: 'Surface', hint: 'Default raised surface', light: '#FFFFFF', dark: '#152331' },
      { token: 'surface_alt', label: 'Surface Alt', hint: 'Secondary surface, e.g. section headers', light: '#EBEBEB', dark: '#1E3040' },
      { token: 'card', label: 'Card', hint: 'Card background', light: '#FFFFFF', dark: '#152331' },
      { token: 'card_alt', label: 'Card Alt', hint: 'Alternate/striped card background', light: '#FAFAFA', dark: '#101C27' },
      { token: 'card_border', label: 'Card Border', hint: 'Card and divider borders', light: '#EBEBEB', dark: '#24384A' },
      { token: 'sheet', label: 'Bottom Sheet', hint: 'Slide-up sheets and modals', light: '#FFFFFF', dark: '#152331' },
    ],
  },
  {
    title: 'Text',
    icon: 'fa-solid fa-font',
    hint: 'Text colours for headings, body copy and secondary help text.',
    tokens: [
      { token: 'heading', label: 'Heading Text', hint: 'Titles and headings', light: '#122331', dark: '#FFFFFF' },
      { token: 'text', label: 'Body Text', hint: 'Default paragraph text', light: '#122331', dark: '#CBD5E1' },
      { token: 'text_muted', label: 'Muted Text', hint: 'Secondary/help text', light: '#6B7280', dark: '#9CA3AF' },
    ],
  },
  {
    title: 'Status',
    icon: 'fa-solid fa-circle-check',
    hint: 'Colours that signal state to participants: success, warning, error, completed and disabled.',
    tokens: [
      { token: 'success', label: 'Success', hint: 'Positive state', light: '#1F9254', dark: '#34D399' },
      { token: 'warning', label: 'Warning', hint: 'Caution state', light: '#D97706', dark: '#F59E0B' },
      { token: 'error', label: 'Error', hint: 'Failure state', light: '#EF233C', dark: '#FF5A6E' },
      { token: 'completed', label: 'Completed', hint: 'Finished task badge', light: '#1F9254', dark: '#34D399' },
      { token: 'disabled', label: 'Disabled', hint: 'Inactive controls', light: '#F1F1F1', dark: '#22303F' },
      { token: 'task', label: 'Task Highlight', hint: 'Background of a pending task row', light: '#FFF6D9', dark: '#3A2B04' },
    ],
  },
  {
    title: 'Gradient',
    icon: 'fa-solid fa-fill-drip',
    hint: 'The two ends of the hero gradient shown at the top of the participant page.',
    tokens: [
      { token: 'gradient_start', label: 'Gradient Start', hint: 'Top of the hero gradient', light: '#FFEAB0', dark: '#4A3505' },
      { token: 'gradient_end', label: 'Gradient End', hint: 'Bottom of the hero gradient', light: '#FFFFFF', dark: '#0D1620' },
    ],
  },
  {
    title: 'Neutrals',
    icon: 'fa-solid fa-circle-half-stroke',
    hint: 'Greys used for borders, dividers and subtle backgrounds, from lightest to darkest.',
    tokens: [
      { token: 'grey_1', label: 'Grey 1', hint: 'Lightest neutral', light: '#E5E7EB', dark: '#22303F' },
      { token: 'grey_2', label: 'Grey 2', hint: 'Neutral', light: '#D1D5DB', dark: '#3C4B5C' },
      { token: 'grey_3', label: 'Grey 3', hint: 'Neutral', light: '#9CA3AF', dark: '#6B7A8C' },
      { token: 'grey_4', label: 'Grey 4', hint: 'Neutral', light: '#6B7280', dark: '#9CA3AF' },
      { token: 'grey_5', label: 'Grey 5', hint: 'Neutral', light: '#3B475C', dark: '#C0C4CB' },
      { token: 'grey_6', label: 'Grey 6', hint: 'Darkest neutral', light: '#122331', dark: '#E5E7EB' },
    ],
  },
];

/** Flat list of every colour token, in group order. */
export const BRAND_COLOR_TOKENS: BrandColorToken[] = BRAND_COLOR_GROUPS.reduce(
  (all, group) => all.concat(group.tokens),
  [] as BrandColorToken[]
);

/** Form control name for a token in the given theme. */
export function colorControlName(token: string, dark = false): string {
  return dark ? `dark_${token}_color` : `${token}_color`;
}
